// The Mileage Log (#476), the web's adapter.
//
// The rules are `core/src/mileage.rs`, whose header records what MyMusicStaff
// does — the drawer's fields, their order, which are optional, the unit
// following the studio's Region — and where this knowingly stops short of it.
// This file turns the providers' documents and the drawer's state into the
// core's records and back: a trip document into a `MileageTrip`, the core's
// refusals into the `{ field: sentence }` map the drawer renders, and its
// document rows into the plain object a batch writes.
//
// No rule lives here. A distance is whole hundredths of the unit it was
// logged in (`distanceHundredths`, `unit`), for the reason money is whole
// minor units; the core says so beside the code that decides it. A trip can
// be a series (#519) — one document holding its rule, by the expense entry's
// names — and a save can make expenses beside it (#520); both are the core's
// too, and the core's header records MMS's behaviour. Every action's
// writes come from the core as one ordered list (#554: `planAdd`,
// `planEdit`, `planDelete`, …), which `planWrites` turns into the plain
// objects a batch takes; and so do the overview's folds (#521).
import { coreSync } from './core.js';
import { documentFromRows } from './documents.js';

const str = (v) => (v === null || v === undefined ? null : String(v));
// A stored count as the core reads one: the number, or none for anything
// that is not a number — a string is not coerced, as the rules never let one
// be written.
const count = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** A `mileageEntries` document as the core's `MileageTrip`. */
export function mileageTripOf(d = {}) {
  return {
    id: String(d?.id ?? ''),
    occurredOn: str(d?.occurredOn),
    destination: str(d?.destination),
    distanceHundredths: count(d?.distanceHundredths),
    unit: str(d?.unit),
    durationMin: count(d?.durationMin),
    vehicle: str(d?.vehicle),
    odometerHundredths: count(d?.odometerHundredths),
    notes: str(d?.notes),
    recurrence: str(d?.recurrence),
    frequency: str(d?.frequency),
    interval: count(d?.interval),
    endDate: str(d?.endDate),
    // The dates that have left a series (#554); a string list, anything else none.
    exceptions: Array.isArray(d?.exceptions) ? d.exceptions.filter((x) => typeof x === 'string') : [],
  };
}

const formOf = (f = {}) => ({
  occurredOn: String(f.occurredOn ?? ''),
  destination: String(f.destination ?? ''),
  distance: String(f.distance ?? ''),
  duration: String(f.duration ?? ''),
  vehicle: String(f.vehicle ?? ''),
  odometer: String(f.odometer ?? ''),
  notes: String(f.notes ?? ''),
  unit: String(f.unit ?? ''),
  // The Recurrence section, the blank drawer's where a caller has none.
  recurrence: String(f.recurrence ?? 'one_time'),
  frequency: String(f.frequency ?? 'weekly'),
  interval: String(f.interval ?? '1'),
  repeatIndefinitely: f.repeatIndefinitely ?? true,
  endDate: String(f.endDate ?? ''),
});

/** The ceilings each box is held to, for its `maxLength`. */
export function mileageLimits() {
  return coreSync().mileageLimits();
}

/** A sentence by code; the code itself for one nobody wrote. */
export function mileageMessage(code) {
  return coreSync().mileageMessage(String(code)) ?? String(code);
}

/** `Miles` or `Kilometers`, as MMS writes the unit beside a box. */
export function unitLabel(unit) {
  return coreSync().mileageUnitLabel(unit ?? null);
}

/** Hundredths with their unit: `12.5 mi`. */
export function formatDistance(hundredths, unit) {
  return coreSync().mileageFormatDistance(Number(hundredths), unit ?? null);
}

/** Hundredths of one unit as hundredths of another, to the nearest hundredth. */
export function convertDistance(hundredths, from, to) {
  return coreSync().mileageConvert(Number(hundredths), from ?? null, to ?? null);
}

// A number box read: `{ hundredths }`/`{ minutes }` (null for an empty
// optional box), or `{ code }` for a refusal.
export function parseDistance(text) {
  const r = coreSync().mileageParseDistance(String(text ?? ''));
  return r.code ? { code: r.code } : { hundredths: r.value };
}
export function parseOdometer(text) {
  const r = coreSync().mileageParseOdometer(String(text ?? ''));
  return r.code ? { code: r.code } : { hundredths: r.value ?? null };
}
export function parseDuration(text) {
  const r = coreSync().mileageParseDuration(String(text ?? ''));
  return r.code ? { code: r.code } : { minutes: r.value ?? null };
}

/** A blank drawer, dated `today`, in the studio's unit. */
export function blankTripForm(today, unit) {
  return coreSync().mileageBlankForm(String(today), unit ?? null);
}

/**
 * A stored trip as the drawer reopens it, in its own unit, its Distance and
 * Odometer with the studio's Region's `decimal` separator — `12,5` in Germany
 * (#583). The box reads either back.
 */
export function tripFormFor(trip, studioUnit, decimal) {
  return coreSync().mileageFormForTrip(mileageTripOf(trip), studioUnit ?? null, decimal);
}

/** Everything Save refuses, as `{ field: sentence }` in the drawer's order. */
export function tripErrors(form) {
  const out = {};
  for (const e of coreSync().mileageTripErrors(formOf(form))) out[e.field] = e.message;
  return out;
}

/** Whether Save is clickable — not whether it will go through. */
export function tripCanSave(form) {
  return coreSync().mileageCanSave(formOf(form));
}

/** Whether the drawer has moved from what it opened with (`baseline`, or a blank one). */
export function tripIsDirty(form, baseline, today, unit) {
  return coreSync().mileageIsDirty(formOf(form), baseline ? formOf(baseline) : null, String(today), unit ?? null);
}

/** The refusals left standing once the `changed` fields are edited. */
export function errorsAfterPatch(errors = {}, changed = []) {
  const kept = coreSync().mileageErrorsAfterPatch(Object.keys(errors), changed.map(String));
  return Object.fromEntries(kept.map((k) => [k, errors[k]]));
}

/** A new trip's document, `createdAt` the sentinel handed in; null for a refused drawer. */
export function tripDocument(form, serverTimestamp) {
  const rows = coreSync().mileageTripDocument(formOf(form));
  return rows ? documentFromRows(rows, serverTimestamp) : null;
}

/** An edit's changed fields, merged onto the trip; `{}` for none, null for a refused drawer. */
export function tripUpdateDocument(before, after) {
  const rows = coreSync().mileageTripUpdateDocument(formOf(before), formOf(after));
  return rows ? documentFromRows(rows) : null;
}

/**
 * The log newest first, each row with the document it was drawn from. A
 * recurring trip is a row per date through the end of the year (#554), each
 * with `series` set and the one trip document behind it.
 */
export function mileageRows(trips = [], studioUnit, today) {
  return coreSync()
    .mileageListRows(trips.map(mileageTripOf), studioUnit ?? null, String(today))
    .map((r) => ({ ...r, trip: trips[r.index] }));
}

/**
 * A row's Edit (#554): the drawer at the row's date for a date of a series,
 * as MMS opens one, else at the trip's own. Its Distance and Odometer in the
 * Region's `decimal`.
 */
export function tripFormForDate(trip, date, studioUnit, decimal) {
  return coreSync().mileageFormForDate(mileageTripOf(trip), date ?? '', studioUnit ?? null, decimal);
}

// ── Recurring trips (#519) ──────────────────────────────────────────────────

/** A recurring trip's Frequency choices, as stored, in the select's order. */
export function mileageFrequencies() {
  return coreSync().mileageFrequencies();
}

/** The Repeats Every box read: `{ interval }` or `{ code }`. */
export function parseInterval(text) {
  const r = coreSync().mileageParseInterval(String(text ?? ''));
  return r.code ? { code: r.code } : { interval: r.value };
}

// ── Automatic mileage expenses (#520) ───────────────────────────────────────

/**
 * Business Settings' Mileage Log tab as a trip's save reads it: the studio
 * record's `mileage` group (`mileageSettingsOf` in ./studio.js), with the
 * studio's unit — which the rate is per — and its currency.
 */
export function expenseSettingsFor(mileage = {}, unit, currency) {
  return {
    enabled: mileage?.autoExpenses === true,
    rate: Number.isInteger(mileage?.rateTenThousandths) ? mileage.rateTenThousandths : 0,
    category: String(mileage?.expenseCategory ?? ''),
    unit: String(unit ?? ''),
    currency: currency ?? null,
  };
}

/** A rate as its box shows it, to four places: `0.6700`. */
export function rateText(tenThousandths) {
  return coreSync().mileageRateText(Number(tenThousandths) || 0);
}

/** The Rate box read: `{ rate }` in ten-thousandths, or `{ code }`. */
export function parseRate(text) {
  const r = coreSync().mileageParseRate(String(text ?? ''));
  return r.code ? { code: r.code } : { rate: r.value };
}

/** What the Rate box commits when it is left: the rate it reads as, or `current`. */
export function rateDraft(text, current) {
  return coreSync().mileageRateDraft(String(text ?? ''), Number(current) || 0);
}

/** `per mile` or `per km`, by the studio's unit. */
export function rateUnitLabel(unit) {
  return coreSync().mileageRateUnitLabel(unit ?? null);
}

// The rate for a sentence a teacher reads, not for the Rate box (#603).
//
// The Rate box affixes the currency sign before the figure in every Region,
// `€ 0,0000`, as MMS's own box does (#549 — the comment above `rateShown` in
// MileagePanel.jsx). The Update Mileage Expenses confirmation (#554) is ours,
// not MMS's, so it writes the figure the way every other sentence in this app
// writes money (#603): the sign where the studio's Region puts it
// (`Intl.NumberFormat`'s `currency` style), after the figure in `de-DE`.
// `format` in ./money.js stays at each currency's own exponent (two places for
// EUR); the rate is always four, so this calls `Intl.NumberFormat` itself — on
// the exact string `rateText` produces, never a `Number`, for the reason
// `format`'s own comment gives.
export function rateMoneyText(tenThousandths, currency, locale, unit) {
  const money = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(rateText(tenThousandths));
  return `${money} ${rateUnitLabel(unit)}`;
}

// ── The writes (#554) ───────────────────────────────────────────────────────

/** "This entry only" / "Only this entry", and the other answer. */
export const THIS_ENTRY = 'this';
export const FOLLOWING = 'following';

/**
 * A core plan as the writes a batch takes, in order: `{ collection, id,
 * kind, document }`, `document` undefined for a delete. `sentinels` are the
 * writer's own `serverTimestamp` value and `arrayUnion` function — this file
 * imports no Firebase.
 */
export function planWrites(plan, { serverTimestamp, arrayUnion } = {}) {
  return (plan || []).map((w) => ({
    collection: w.collection,
    id: w.id,
    kind: w.kind,
    document: w.kind === 'delete' ? undefined : documentFromRows(w.document, serverTimestamp, { arrayUnion }),
  }));
}

const settingsOf = (s) => s ?? expenseSettingsFor(undefined, 'mi', null);

/** A new trip's Save: the trip under `id`, then its expenses. Null for a refused drawer. */
export function planAdd(id, form, settings, today, sentinels) {
  const plan = coreSync().mileagePlanAdd(String(id), formOf(form), settingsOf(settings), String(today));
  return plan ? planWrites(plan, sentinels) : null;
}

/**
 * An edit's Save: `trip` as stored, `date` the row's, `scope` the answer to
 * "Update Recurring Entries" for a date of a series. `[]` for nothing
 * changed, null for a refused drawer.
 */
export function planEdit(trip, date, scope, before, after, settings, today, sentinels) {
  const plan = coreSync().mileagePlanEdit(
    mileageTripOf(trip), String(date ?? ''), String(scope ?? THIS_ENTRY), formOf(before), formOf(after),
    settingsOf(settings), String(today),
  );
  return plan ? planWrites(plan, sentinels) : null;
}

/** Delete at `date`, answered `scope`. */
export function planDelete(trip, date, scope, today, sentinels) {
  return planWrites(
    coreSync().mileagePlanDelete(mileageTripOf(trip), String(date ?? ''), String(scope ?? THIS_ENTRY), String(today)),
    sentinels,
  );
}

/** "Yes, update the existing mileage log entries", Starting On `from`. */
export function planUpdateExisting(trips = [], settings, from, today) {
  return planWrites(coreSync().mileagePlanUpdateExisting(trips.map(mileageTripOf), settingsOf(settings), String(from ?? ''), String(today)));
}

/** What a series is owed, given the expense ids the studio holds: the year's turn, and a series from before #554. */
export function planTopUp(trips = [], presentIds = [], settings, today) {
  return planWrites(coreSync().mileagePlanTopUp(trips.map(mileageTripOf), presentIds.map(String), settingsOf(settings), String(today)));
}

/** Whether an expense id — or a recurring instance's rule's — is the Mileage Log's, which the Expenses list will not open. */
export function isMileageExpenseId(id) {
  return typeof id === 'string' && coreSync().mileageIsExpenseId(id);
}

/** A trip's expense description, MMS's `Sweep Hall (13 mi)`. */
export function expenseDescription(trip) {
  return coreSync().mileageExpenseDescription(mileageTripOf(trip));
}

// ── The overview (#521) ─────────────────────────────────────────────────────

/** "Repeat Trip": a new trip today with the trip's destination, distance, duration and vehicle. */
export function repeatTripForm(trip, today, studioUnit, decimal) {
  return coreSync().mileageRepeatTripForm(mileageTripOf(trip), String(today), studioUnit ?? null, decimal);
}

/** The fields "Repeat Trip" fills, which the drawer marks as autofilled. */
export function repeatTripFields() {
  return coreSync().mileageRepeatTripFields();
}

/** The Recent Trips cards, each with the trip document it was drawn from. */
export function recentTrips(trips = [], studioUnit, today) {
  return coreSync()
    .mileageRecentTrips(trips.map(mileageTripOf), studioUnit ?? null, String(today))
    .map((r) => ({ ...r, trip: trips[r.index] }));
}

/** "Annual summary for" `year`: its total and its months, newest first. */
export function yearSummary(trips = [], studioUnit, year, today) {
  return coreSync().mileageYearSummary(trips.map(mileageTripOf), studioUnit ?? null, String(year), String(today));
}

/** The years the summary's picker offers, newest first. */
export function summaryYears(trips = [], today) {
  return coreSync().mileageSummaryYears(trips.map(mileageTripOf), String(today));
}

/** "Trips in" `month` (`YYYY-MM`), searched and sorted, each with its trip document. */
export function monthRows(trips = [], studioUnit, month, today, query = {}) {
  const q = {
    sort: String(query.sort ?? ''), desc: Boolean(query.desc), text: String(query.text ?? ''),
    onlyRepeating: Boolean(query.onlyRepeating),
  };
  return coreSync()
    .mileageMonthRows(trips.map(mileageTripOf), studioUnit ?? null, String(month), String(today), q)
    .map((r) => ({ ...r, trip: trips[r.index] }));
}

/** The month page's Columns menu, `{ key, label, shown }`, in MMS's order. */
export function monthColumns(unit) {
  return coreSync().mileageMonthColumns(unit ?? null);
}

/** The month page's Sort menu, `{ key, label }`, in MMS's order. */
export function monthSortColumns(unit) {
  return coreSync().mileageMonthSortColumns(unit ?? null).map(({ key, label }) => ({ key, label }));
}

/** A header with the studio's unit: `Total Distance (mi)`. */
export function withUnit(label, unit) {
  return coreSync().mileageWithUnit(String(label), unit ?? null);
}

/** The last date a series is listed through: the end of the year. */
export function horizon(today, first) {
  return coreSync().mileageHorizon(String(today), first ?? '');
}

/** A card's distance, `12.5 mi traveled`, and a year's, `200 Miles traveled`. */
export function traveledText(hundredths, unit) {
  return coreSync().mileageTraveledText(Number(hundredths), unit ?? null);
}
export function yearTotalText(hundredths, unit) {
  return coreSync().mileageYearTotalText(Number(hundredths), unit ?? null);
}

/** The confirmation in front of the update: the count, Starting On as written, the rate as shown, the category. */
export function updateExistingMessage(trips, from, rate, category) {
  return coreSync().mileageUpdateExistingMessage(Number(trips) || 0, String(from ?? ''), String(rate), String(category ?? ''));
}

/** What the tab says once the update is queued. */
export function updateExistingDone(trips) {
  return coreSync().mileageUpdateExistingDone(Number(trips) || 0);
}
